/**
 * How Rocky's clues are laid out on the television, by how many there are.
 *
 * The clue list is the only thing on any screen in this app whose height is
 * set by the puzzle rather than by the design. A riddle at level nine can
 * carry fourteen clues where the one she plays today carries three, and at the
 * size DOL-58 set — which was the right size, the clues did read small from a
 * sofa — fourteen of them want more than twice the screen.
 *
 * So the size follows the clues. Not how *many* there are, which is the first
 * thing this measured and the wrong thing: `riddle-skills.ts` says outright
 * that clue count is not a difficulty measure, and it is not a height measure
 * either. A `link` or a `side` clue is a much longer sentence than a `direct`
 * one, so four clues at level six can be taller than five at level four. A
 * sweep of sixty dealt riddles had eleven of them overflowing the screen on a
 * count-based tier, including a four-clue riddle — inside the range she is
 * dealt today.
 *
 * What it goes on instead is the total number of characters, which is close
 * enough to the number of lines to be worth measuring against, and which every
 * one of those sixty riddles agreed with: the boundaries below sit under the
 * longest riddle that was measured to fit at each size.
 *
 * Tiered rather than continuous on purpose: three sizes can each be measured
 * and held to, and a formula that interpolates is a formula nobody has seen
 * the output of.
 *
 * The point of stopping at "smaller" rather than shrinking until anything
 * fits: a logic puzzle is the one place where seeing everything at once *is*
 * the task. Deduction means holding two constraints together, and a clue list
 * you have to scroll turns that into a memory test. So this shrinks as far as
 * stays readable across a room and no further — and the television frame
 * (DOL-66) carries whatever is still over, which by then is a riddle she will
 * not meet for years.
 *
 * Each string carries its own `data-scale` prefix rather than being prefixed
 * where it is used. Half of one of Sky's did reach the phone that way.
 */
export interface ClueLayout {
  /**
   * Up to and including this many characters of clue, all told, this is the
   * layout.
   *
   * This is where the size *steps down*, not a promise that it fits. Until
   * DOL-85 it was both, and the numbers were chosen as the longest riddle
   * seen to fit at each size. Fitting is no longer what they are for: she
   * writes notes on paper for the hard ones, so the frame carrying the rest
   * costs her nothing, and the sizes went up knowing that.
   *
   * Fit is not a function of length alone anyway — measured over 132 real
   * deals, the largest riddle that fitted at the top size was 148 characters
   * and the smallest that scrolled was 115. How many clues it is split
   * across decides the wrapping, so no single number could have been true.
   * What is actually policed is the outcome, in `television.spec.ts`: no
   * riddle drawn below the body size, and none needing more scroll than a
   * person will wear.
   */
  readonly upTo: number
  readonly className: string
}

export const CLUE_LAYOUTS: readonly ClueLayout[] = [
  {
    /*
     * The size DOL-58 asked for, because the clues read small from a sofa —
     * and two columns are what pay for it.
     *
     * One tier here, not two. DOL-85 raised the middle tier to this same
     * size, which left two entries with byte-identical classNames and a
     * boundary at 130 characters deciding between them: dead config that
     * read as live. Collapsing them changes nothing that renders.
     */
    upTo: 250,
    className:
      '[[data-scale=tv]_&]:grid [[data-scale=tv]_&]:grid-cols-2 [[data-scale=tv]_&]:gap-x-10 [[data-scale=tv]_&]:text-(length:--text-heading)',
  },
  {
    /*
     * The floor, and unbounded on purpose.
     *
     * There is no tier below this one: the next size down is the small print,
     * and a clue nobody can read from the sofa is worse than a clue she has
     * to scroll to. This tier mostly does not fit — of 33 measured deals that
     * reached it, one fitted — and that is the accepted cost, not an
     * oversight. The frame carries what is left over.
     */
    upTo: Number.POSITIVE_INFINITY,
    className:
      '[[data-scale=tv]_&]:grid [[data-scale=tv]_&]:grid-cols-3 [[data-scale=tv]_&]:gap-x-10 [[data-scale=tv]_&]:text-(length:--text-body)',
  },
]

/** How much clue there is to lay out, which is what decides the size. */
export function clueLength(clues: readonly string[]): number {
  return clues.reduce((total, clue) => total + clue.length, 0)
}

/** The layout for these clues. */
export function clueLayout(clues: readonly string[]): ClueLayout {
  const length = clueLength(clues)
  const found = CLUE_LAYOUTS.find((layout) => length <= layout.upTo)

  // The last tier is unbounded, so this cannot happen — but returning
  // `undefined` into a className would silently draw an unstyled list.
  if (!found) throw new Error(`No clue layout for ${length} characters`)

  return found
}
